import { useWikiSpacePages } from "@/hooks/use-wiki-space-pages";
import { useWikiSpaces } from "@/hooks/use-wiki-spaces";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import { useNavigate } from "react-router";
import { findPageLineage } from "./wiki-page-lineage";

/** Props Interface */
interface WikiPageTrailProps {
	spaceId: string;
	pageId: string;
}

/**
 * The space and the pages above the current one, each a link. The page itself is not part of the
 * trail: its title stands beneath as the heading. The app shell's breadcrumb cannot show this
 * chain, because parent pages are not in the URL — only the tree knows them.
 */
export function WikiPageTrail({ spaceId, pageId }: WikiPageTrailProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();

	/** Query */
	const { wikiSpaces } = useWikiSpaces();
	const { wikiSpacePages } = useWikiSpacePages(spaceId);

	/** Computed */
	const space = wikiSpaces?.find((candidate) => candidate.id === spaceId);
	const { ancestors } = findPageLineage(wikiSpacePages, pageId);

	/** Render */
	return (
		<nav className="h-stack min-w-0 text-sm">
			<button
				type="button"
				className="h-stack min-w-0 cursor-pointer opacity-70 hover:opacity-100"
				onClick={() => navigate(`/wiki/${spaceId}`)}
			>
				{space && <StatusIndicator color={space.color} />}
				<span className="truncate">{space?.name ?? guardString(null)}</span>
			</button>
			{ancestors.map((ancestor) => (
				<React.Fragment key={ancestor.id}>
					<Icon
						name="ChevronRight"
						size={12}
						className="shrink-0 opacity-35"
					/>
					<button
						type="button"
						className="min-w-0 cursor-pointer truncate opacity-70 hover:opacity-100"
						onClick={() => navigate(`/wiki/${spaceId}/${ancestor.id}`)}
					>
						{guardString(ancestor.title, t("wiki.untitled"))}
					</button>
				</React.Fragment>
			))}
		</nav>
	);
}
